import type { SlipLadder } from "@/components/optimizer/props-slip-breakevens";
import { parsePick6Link } from "./book-links";

const DK_API = "https://api.draftkings.com";
const TABLE_TTL_MS = 5 * 60_000;
const DK_ID = /^\d+$/;

export type Pick6Picks = { readonly pickGroup: string; readonly picks: readonly string[]; readonly key: string };

export type Pick6Tables = Readonly<Record<number, SlipLadder>>;

export type Pick6Quote = { readonly key: string; readonly pickGroup: string; readonly factor: number; readonly tables: Pick6Tables };

export function pick6Picks(links: readonly (string | null)[]): Pick6Picks | null {
  const parts = links.map(parsePick6Link);
  const first = parts[0];
  if (first === undefined || first === null || !DK_ID.test(first.pickGroup)) return null;
  const picks = new Set<string>();
  for (const part of parts) {
    if (part === null || part.pickGroup !== first.pickGroup) return null;
    for (const pick of part.picks) {
      if (!DK_ID.test(pick)) return null;
      picks.add(pick);
    }
  }
  if (picks.size < 2) return null;
  const sorted = [...picks].sort();
  return { pickGroup: first.pickGroup, picks: sorted, key: `${first.pickGroup}:${sorted.join(",")}` };
}

function isPositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function parseCorrelation(body: unknown): number | null {
  const factor = (body as { correlationFactor?: unknown } | null)?.correlationFactor;
  return isPositive(factor) ? factor : null;
}

export function parsePayoutTables(body: unknown): Pick6Tables | null {
  const packages = (body as { payoutPackages?: unknown } | null)?.payoutPackages;
  if (!Array.isArray(packages)) return null;
  const tables: Record<number, SlipLadder> = {};
  for (const entry of packages as { pickSetSize?: unknown; payoutTiers?: unknown }[]) {
    if (!isPositive(entry?.pickSetSize) || !Array.isArray(entry.payoutTiers)) continue;
    const ladder: Record<number, number> = {};
    for (const tier of entry.payoutTiers as { numberOfPicksCorrect?: unknown; guaranteedMultiplier?: unknown }[]) {
      if (isPositive(tier?.numberOfPicksCorrect) && isPositive(tier.guaranteedMultiplier)) ladder[tier.numberOfPicksCorrect] = tier.guaranteedMultiplier;
    }
    if (ladder[entry.pickSetSize] !== undefined) tables[entry.pickSetSize] = ladder;
  }
  return Object.keys(tables).length > 0 ? tables : null;
}

async function dkJson(url: string, init: RequestInit): Promise<unknown> {
  const response = await fetch(url, { ...init, credentials: "omit" });
  if (!response.ok) throw new Error(`DK Pick6 answered ${response.status}`);
  return response.json();
}

const heldTables = new Map<string, { readonly atMs: number; readonly tables: Promise<Pick6Tables> }>();

function payoutTables(pickGroup: string, nowMs: number): Promise<Pick6Tables> {
  const held = heldTables.get(pickGroup);
  if (held !== undefined && nowMs - held.atMs < TABLE_TTL_MS) return held.tables;
  const tables = dkJson(`${DK_API}/pick6/v1/entrydetails/${pickGroup}?format=json`, {}).then((body) => {
    const parsed = parsePayoutTables(body);
    if (parsed === null) throw new Error("DK Pick6 sent no payout tables");
    return parsed;
  });
  heldTables.set(pickGroup, { atMs: nowMs, tables });
  tables.catch(() => heldTables.delete(pickGroup));
  return tables;
}

export async function fetchPick6Quote(picks: Pick6Picks, signal: AbortSignal, nowMs = Date.now()): Promise<Pick6Quote> {
  const form = new FormData();
  form.append("pickGroupId", picks.pickGroup);
  form.append("pickableMarketSelectionIds", `[${picks.picks.join(",")}]`);
  form.append("requestId", crypto.randomUUID());
  const [body, tables] = await Promise.all([
    dkJson(`${DK_API}/pick6-entry/entries/v1/correlation?format=json`, { method: "POST", body: form, signal }),
    payoutTables(picks.pickGroup, nowMs),
  ]);
  const factor = parseCorrelation(body);
  if (factor === null) throw new Error("DK Pick6 sent no correlation factor");
  return { key: picks.key, pickGroup: picks.pickGroup, factor, tables };
}
